'use client';

import {
  ManifestForm,
  type ManifestFormProps,
} from '@modules/manifest';
import {
  getPersistedPin,
  setPersistedPin,
} from '@modules/prism-client/lib/manifest/pin-storage';
import { storeManifestAction } from '@modules/prism-client/lib/manifest/store-manifest-action';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

import type {
  ManifestFieldErrors,
  ManifestFormValues,
} from '@modules/manifest';
import type { ComponentBlock } from '@modules/prism-client/types/types';
import type { Account } from '@tuic/api-kit';

export type ManifestFormBlockProps = ManifestFormProps & {
  traveller: { mpi: number; pin?: string };
};

const emptyToNull = <T,>(value: T): T | null =>
  typeof value === 'string' && value === '' ? null : value;

const sanitizeSection = <T extends Record<string, unknown>>(
  section: T,
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(section)) {
    out[key] = emptyToNull(section[key]);
  }
  return out;
};

const reshapeOtherInformation = (
  values: ManifestFormValues['otherInformation'],
  salutation: string,
): Account.ManifestOtherInformation => {
  const {
    mealRequest,
    individualRequest,
    allergy,
    allergyInfo,
    emergencyContactName,
    emergencyContactPhone,
    handicap,
    acceptDataProtectionTerms,
    acceptHealthTerms,
    pregnant24Weeks,
  } = values;

  // TBD: Backend erzwingt 'NotPregnant24Weeks must be True'. Nicht-Frauen sehen
  // die Checkbox nicht (siehe OtherInformationSection), können also nichts
  // bestätigen — wir schicken hardcoded `true`, damit das Manifest überhaupt
  // speicherbar ist. Entfernen, sobald Backend `null` akzeptiert oder die Regel
  // aufgeweicht wird.
  const isFemale = salutation === 'Frau';

  // Gesundheitsdaten-Einwilligung nur senden, wenn tatsächlich Gesundheitsdaten
  // (Ernährung oder Behinderung) ausgefüllt wurden. Wenn alle fünf Felder leer
  // sind, schicken wir `null` — auch wenn der Nutzer zwischendurch zugestimmt
  // hatte und die Felder wieder entfernt hat.
  const hasHealthData =
    !!mealRequest ||
    !!individualRequest ||
    allergy.length > 0 ||
    !!allergyInfo ||
    !!handicap;

  return {
    handicap: handicap || null,
    acceptDataProtectionTerms: hasHealthData ? acceptDataProtectionTerms : null,
    acceptHealthTerms,
    pregnant24Weeks: isFemale ? pregnant24Weeks : true,
    dietary: {
      mealRequest: mealRequest || null,
      individualRequest: individualRequest || null,
      allergy: allergy.length > 0 ? allergy : undefined,
      allergyInfo: allergyInfo || null,
    },
    emergencyContact: {
      name: emergencyContactName || null,
      phone: emergencyContactPhone || null,
    },
  };
};

const reshapeForApi = (values: ManifestFormValues): Account.ManifestDetail =>
  ({
    personalInfo: sanitizeSection(values.personalInfo),
    contact: sanitizeSection(values.contact),
    credentials: sanitizeSection(values.credentials),
    otherInformation: reshapeOtherInformation(
      values.otherInformation,
      values.personalInfo.salutation,
    ),
    preferredLanguageCode: values.otherInformation.preferredLanguageCode || null,
  }) as Account.ManifestDetail;

export const ManifestFormBlock: ComponentBlock<ManifestFormBlockProps> = ({
  data,
  fallback,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const [isPinRequired, setIsPinRequired] = useState<boolean | undefined>(
    undefined,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<ManifestFieldErrors | null>(
    null,
  );
  const [hasGenericError, setHasGenericError] = useState(false);

  useEffect(() => {
    if (data?.traveller?.pin) {
      const persistedPin = getPersistedPin(data.traveller.mpi);
      setIsPinRequired(data.traveller.pin !== persistedPin);
    } else setIsPinRequired(false);
  }, [data?.traveller]);

  const handlePinEnter = (enteredPin: string): boolean => {
    if (!data?.traveller) return false;

    const pin = data.traveller.pin;
    if (pin !== enteredPin) return false;

    setPersistedPin(data.traveller.mpi, enteredPin);
    setIsPinRequired(false);
    return true;
  };

  const handleSubmit = useCallback(
    async (formValues: ManifestFormValues) => {
      if (!data?.traveller?.mpi) {
        return;
      }

      setFieldErrors(null);
      setHasGenericError(false);
      setIsSubmitting(true);

      const body = reshapeForApi(formValues);
      const result = await storeManifestAction(data.traveller.mpi, body);

      setIsSubmitting(false);

      if (result.isSuccess) {
        // Redirect to the editorial success page. Slug pattern is fixed by
        // the Contentful FlexPage: form sits at `/…/lukas-manifest-form/{id}`
        // and success at `/…/lukas-manifest-form-success/{id}` — same trailing
        // traveller id, so a path-segment replace is sufficient. Fall back to
        // a data refresh if the current path doesn't match (shouldn't happen).
        const successPath = pathname.replace(
          '/lukas-manifest-form/',
          '/lukas-manifest-form-success/',
        );
        if (successPath !== pathname) {
          router.push(successPath);
        } else {
          router.refresh();
        }
      } else if (result.fieldErrors) {
        setFieldErrors(result.fieldErrors);
      } else if (result.hasGenericError) {
        setHasGenericError(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    [data?.traveller?.mpi, pathname, router],
  );

  return (
    <ManifestForm
      {...data}
      manifest={data?.manifest}
      fallback={isPinRequired !== false || (fallback ?? data?.fallback)}
      isPinRequired={isPinRequired}
      onPinEnter={handlePinEnter}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      fieldErrors={fieldErrors}
      hasGenericError={hasGenericError}
    />
  );
};
